import { action } from 'mobx';
import type { CreateTarget } from 'actions';
import { Aggregate } from 'edit/Aggregate';
import type { EditableEntity } from 'edit/EditableEntity';
import { Property } from 'edit/Property';
import type { FolderReference } from 'edit/reference/EditFolderReference';
import type { ZoneReference } from 'edit/reference/EditZoneReference';
import type { RecurrenceDefinition } from 'api/recurrence_definitions/endpoints/getRecurrenceDefinition';
import type { SaveRecurrenceDefinition } from 'api/recurrence_definitions/endpoints/types';
import { formatFolderPath } from 'helpers/folderPaths';
import { EditableRecurrence } from './EditableRecurrence';

const DEFAULT_RESUBMIT_ON_ERROR = true;

export interface EditableRecurrenceDefinitionOptions {
  /** Pre-fill from a loaded record but treat as a new record (POST, cleared name). */
  readonly duplicate?: boolean;
  /** Workspace create pre-target: seeds the zone + folder of a blank create form. */
  readonly createIn?: CreateTarget;
}

/**
 * A recurrence definition open for editing: its own fields plus a one-to-many
 * aggregate of `EditableRecurrence` rules. Built directly from the loaded wire
 * record (or `null` for a blank create form) — there is no intermediate
 * "editable data" type; the model *is* the editable state, and `toSave`
 * projects straight back to the request body.
 *
 * `original` is the pre-edit identity used to choose POST vs PUT and to address
 * the PUT URL; `null` means a not-yet-saved record. It flips to a real identity
 * after the first successful save (via `reinitialize` with the server response).
 *
 * `isDirty` is "create is always dirty, otherwise the current `toSave` body
 * differs from the snapshot captured at load/reinitialize" — a structural
 * comparison that covers the definition fields and every recurrence, and
 * correctly reads clean again if the user reverts an edit by hand.
 */
export class EditableRecurrenceDefinition implements EditableEntity<RecurrenceDefinition | null> {
  private readonly _description = new Property<string | null>(null);
  private readonly _folder = new Property<FolderReference | null>(null);
  private readonly _name = new Property<string | null>(null);
  private readonly _original = new Property<{
    zoneName: string;
    name: string;
  } | null>(null);
  private readonly _recurrences = new Aggregate<EditableRecurrenceDefinition, EditableRecurrence>(this, (aggregate) => new EditableRecurrence(aggregate));
  private readonly _resubmitOnError = new Property<boolean>(DEFAULT_RESUBMIT_ON_ERROR);
  private _snapshot = '';
  private readonly _zone = new Property<ZoneReference | null>(null);

  constructor(loaded: RecurrenceDefinition | null, options?: EditableRecurrenceDefinitionOptions) {
    this._init(loaded, options?.duplicate ?? false, options?.createIn);
  }

  get description(): string | null {
    return this._description.get();
  }
  setDescription = this._description.set;

  get folder(): FolderReference | null {
    return this._folder.get();
  }
  setFolder = this._folder.set;

  get isCreating(): boolean {
    return this._original.get() === null;
  }

  get isDirty(): boolean {
    return this._original.get() === null || JSON.stringify(this.toSave()) !== this._snapshot;
  }

  get name(): string | null {
    return this._name.get();
  }
  setName = this._name.set;

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this
   * to choose create vs update and to build the PUT URL.
   */
  get original(): { zoneName: string; name: string } | null {
    return this._original.get();
  }

  get recurrences(): readonly EditableRecurrence[] {
    return this._recurrences.children;
  }

  get resubmitOnError(): boolean {
    return this._resubmitOnError.get();
  }
  setResubmitOnError = this._resubmitOnError.set;

  get zone(): ZoneReference | null {
    return this._zone.get();
  }
  setZone = this._zone.set;

  /** Append a fresh, empty recurrence rule and return it. */
  createRecurrence = (): EditableRecurrence => this._recurrences.create();

  /**
   * Reset to the loaded record (or a blank create form when `null`) and
   * recapture the dirty baseline. Called after a successful load or save.
   */
  reinitialize = (loaded: RecurrenceDefinition | null): void => {
    this._init(loaded);
  };

  toSave(): SaveRecurrenceDefinition {
    return {
      name: this._name.get(),
      description: this._description.get(),
      zone_name: this._zone.get()?.name ?? null,
      resubmit_on_error: this._resubmitOnError.get(),
      folder_id: this._folder.get()?.folder_id ?? null,
      recurrences: this._recurrences.children.map((r) => r.toSave()),
    };
  }

  // Shared by the constructor and `reinitialize`. Rebuilds the recurrence
  // children in place (same aggregate instance, so reactivity survives) and
  // snapshots the resulting save body for dirty tracking. Wrapped in `action`
  // so the whole rebuild — every field plus the clear/append of the children —
  // lands as one transaction; observers see only the final state, never a
  // half-applied intermediate, and re-render once rather than once per `.set()`.
  private readonly _init = action((loaded: RecurrenceDefinition | null, duplicate = false, createIn?: CreateTarget): void => {
    this._recurrences.clear();
    if (loaded !== null) {
      if (loaded.zone_name === null) {
        throw new Error(`Recurrence definition '${loaded.name}' has no zone (orphaned by zone deletion); cannot be edited.`);
      }
      if (duplicate) {
        this._original.set(null);
        this._name.set(null);
      } else {
        this._original.set({ zoneName: loaded.zone_name, name: loaded.name });
        this._name.set(loaded.name);
      }
      this._zone.set({ name: loaded.zone_name });
      this._folder.set(
        loaded.folder_id !== null
          ? {
              folder_id: loaded.folder_id,
              path: formatFolderPath(loaded.folder_path),
            }
          : null
      );
      this._description.set(loaded.description);
      this._resubmitOnError.set(loaded.resubmit_on_error ?? DEFAULT_RESUBMIT_ON_ERROR);
      for (const wire of loaded.recurrences) {
        this._recurrences.append(new EditableRecurrence(this._recurrences, wire));
      }
    } else {
      this._original.set(null);
      this._name.set(null);
      // Seed the zone/folder from the workspace create pre-target when present.
      this._zone.set(createIn ? { name: createIn.zoneName } : null);
      this._folder.set(createIn?.folderId != null ? { folder_id: createIn.folderId, path: '' } : null);
      this._description.set(null);
      this._resubmitOnError.set(DEFAULT_RESUBMIT_ON_ERROR);
      this._recurrences.append(new EditableRecurrence(this._recurrences));
    }
    this._snapshot = JSON.stringify(this.toSave());
  });
}
